import {
  Checklist,
  Section,
  SupportingItemInput,
  RequestedSupportingItem,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import {
  ChecklistDTO,
  SectionDTO,
  SupportingItemInputTypeDTO,
  SupportingItemInputTypeDiscriminatorDTO,
  RequestedSupportingItemDTO,
  DocumentSupportingItemInputTypeDTO,
  PersonalInfoSupportingItemInputTypeDTO,
  SpousalInfoSupportingItemInputTypeDTO,
  DependentsInfoSupportingItemInputTypeDTO,
  PersonalRefundMethodInfoSupportingItemInputTypeDTO,
  BusinessInfoSupportingItemInputTypeDTO,
  BusinessRefundMethodInfoSupportingItemInputTypeDTO,
  NotesSupportingItemInputTypeDTO,
} from './info-gathering'
import { BadRequest } from 'fejl'
import { timestampToISO } from '../../utils/grpc-util'

export function mapChecklistProtoToDTO(
  proto: Checklist,
  jobId: string,
): ChecklistDTO {
  return {
    job_id: jobId,
    sections: proto
      .getSectionsList()
      .map((section) => mapSectionProtoToDTO(section)),
  }
}

function mapSectionProtoToDTO(proto: Section): SectionDTO {
  return {
    id: proto.getId(),
    name: proto.getName(),
    heading: proto.getHeading(),
    description: proto.getDescription(),
    requested_supporting_items: proto
      .getRequestedSupportingItemsList()
      .map((requestedSupportingItem) =>
        mapSupportingItemToDto(requestedSupportingItem),
      ),
  }
}

export function mapSupportingItemToDto(
  proto: RequestedSupportingItem,
): RequestedSupportingItemDTO {
  return {
    id: proto.getId(),
    name: proto.getName(),
    heading: proto.getHeading(),
    short_description: proto.getShortDescription(),
    long_description: proto.getLongDescription(),
    image_url: proto.getImageUrl()?.getValue(),
    preview_url: proto.getPreviewUrl()?.getValue(),
    required: proto.getRequired(),
    adHoc: proto.getAdHoc(),
    completed: proto.getCompleted(),
    update_time: timestampToISO(proto.getUpdateTime()),
    input: {
      type: mapSupportingItemInputType(proto.getInput()),
    },
  }
}

function mapSupportingItemInputType(
  supportingItemInput?: SupportingItemInput,
): SupportingItemInputTypeDTO {
  if (!supportingItemInput) {
    throw new BadRequest('Supporting item input type not found')
  }

  switch (supportingItemInput.getTypeCase()) {
    case SupportingItemInput.TypeCase.DOCUMENT:
      return mapSupportingItemDocumentInputType(
        supportingItemInput.getDocument(),
      )
    case SupportingItemInput.TypeCase.PERSONAL_INFO:
      return mapSupportingItemPersonalInfoInputType(
        supportingItemInput.getPersonalInfo(),
      )
    case SupportingItemInput.TypeCase.SPOUSAL_INFO:
      return mapSupportingItemSpousalInfoInputType(
        supportingItemInput.getSpousalInfo(),
      )
    case SupportingItemInput.TypeCase.DEPENDENTS_INFO:
      return mapSupportingItemDependentsInfoInputType(
        supportingItemInput.getDependentsInfo(),
      )
    case SupportingItemInput.TypeCase.PERSONAL_REFUND_METHOD:
      return mapSupportingItemPersonalRefundMethodInfoInputType(
        supportingItemInput.getPersonalRefundMethod(),
      )
    case SupportingItemInput.TypeCase.BUSINESS_INFO:
      return mapSupportingItemBusinessInfoInputType(
        supportingItemInput.getBusinessInfo(),
      )
    case SupportingItemInput.TypeCase.BUSINESS_REFUND_METHOD:
      return mapSupportingItemBusinessRefundMethodInfoInputType(
        supportingItemInput.getBusinessRefundMethod(),
      )
    case SupportingItemInput.TypeCase.NOTES:
      return mapSupportingItemNotesInputType(supportingItemInput.getNotes())
    default:
      throw new BadRequest('Unsupported input type')
  }
}

function mapSupportingItemDocumentInputType(
  documentInput?: SupportingItemInput.Document,
): DocumentSupportingItemInputTypeDTO {
  if (!documentInput) {
    throw new BadRequest('Document input type not found')
  }

  return {
    discriminator: SupportingItemInputTypeDiscriminatorDTO.DOCUMENT,
    document: {
      document_type_short_id: documentInput.getDocumentTypeShortId(),
      document_ids: documentInput.getDocumentIdsList(),
    },
  }
}

function mapSupportingItemPersonalInfoInputType(
  personalInfoInput?: SupportingItemInput.PersonalInfo,
): PersonalInfoSupportingItemInputTypeDTO {
  if (!personalInfoInput) {
    throw new BadRequest('Personal info input type not found')
  }

  return {
    discriminator: SupportingItemInputTypeDiscriminatorDTO.PERSONAL_INFO,
    personal_info: {
      profile_id: personalInfoInput.getProfileId()?.getValue(),
      profile_version: personalInfoInput.getProfileVersion()?.getValue(),
    },
  }
}

function mapSupportingItemSpousalInfoInputType(
  spousalInfoInput?: SupportingItemInput.SpousalInfo,
): SpousalInfoSupportingItemInputTypeDTO {
  if (!spousalInfoInput) {
    throw new BadRequest('Spousal info input type not found')
  }

  return {
    discriminator: SupportingItemInputTypeDiscriminatorDTO.SPOUSAL_INFO,
    spousal_info: {
      profile_id: spousalInfoInput.getProfileId()?.getValue(),
      profile_version: spousalInfoInput.getProfileVersion()?.getValue(),
    },
  }
}

function mapSupportingItemDependentsInfoInputType(
  dependentsInfoInput?: SupportingItemInput.DependentsInfo,
): DependentsInfoSupportingItemInputTypeDTO {
  if (!dependentsInfoInput) {
    throw new BadRequest('Dependents info input type not found')
  }

  return {
    discriminator: SupportingItemInputTypeDiscriminatorDTO.DEPENDENTS_INFO,
    dependents: dependentsInfoInput.getDependentsList().map((depend) => {
      return {
        profile_id: depend.getProfileId()?.getValue(),
        profile_version: depend.getProfileVersion()?.getValue(),
      }
    }),
  }
}

function mapSupportingItemPersonalRefundMethodInfoInputType(
  personalRefundMethodInfoInput?: SupportingItemInput.PersonalRefundMethod,
): PersonalRefundMethodInfoSupportingItemInputTypeDTO {
  if (!personalRefundMethodInfoInput) {
    throw new BadRequest('Personal refund method info input type not found')
  }

  return {
    discriminator:
      SupportingItemInputTypeDiscriminatorDTO.PERSONAL_REFUND_METHOD_INFO,
    personal_refund_method_info: {
      refund_method_id: personalRefundMethodInfoInput
        .getRefundMethodId()
        ?.getValue(),
      refund_method_version: personalRefundMethodInfoInput
        .getRefundMethodVersion()
        ?.getValue(),
    },
  }
}

function mapSupportingItemBusinessInfoInputType(
  businessInfoInput?: SupportingItemInput.BusinessInfo,
): BusinessInfoSupportingItemInputTypeDTO {
  if (!businessInfoInput) {
    throw new BadRequest('Business info input type not found')
  }

  return {
    discriminator: SupportingItemInputTypeDiscriminatorDTO.BUSINESS_INFO,
    business_info: {
      profile_id: businessInfoInput.getProfileId()?.getValue(),
      profile_version: businessInfoInput.getProfileVersion()?.getValue(),
    },
  }
}

function mapSupportingItemBusinessRefundMethodInfoInputType(
  businessRefundMethodInfoInput?: SupportingItemInput.BusinessRefundMethod,
): BusinessRefundMethodInfoSupportingItemInputTypeDTO {
  if (!businessRefundMethodInfoInput) {
    throw new BadRequest('Business refund method info input type not found')
  }

  return {
    discriminator:
      SupportingItemInputTypeDiscriminatorDTO.BUSINESS_REFUND_METHOD_INFO,
    business_refund_method_info: {
      refund_method_id: businessRefundMethodInfoInput
        .getRefundMethodId()
        ?.getValue(),
      refund_method_version: businessRefundMethodInfoInput
        .getRefundMethodVersion()
        ?.getValue(),
    },
  }
}

function mapSupportingItemNotesInputType(
  notesInput?: SupportingItemInput.Notes,
): NotesSupportingItemInputTypeDTO {
  if (!notesInput) {
    throw new BadRequest('Notes input type not found')
  }

  return {
    discriminator: SupportingItemInputTypeDiscriminatorDTO.NOTES,
    notes: {
      text: notesInput.getText()?.getValue(),
    },
  }
}
